import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { CredentialType } from "@api/shared-runtime/enums";
import { supportsCredentialImportFieldSelection } from "@api/shared-runtime/credential-import-fields";
import { useProviderImportTransport } from "./use-provider-import-transport";
import {
  canApplyProviderOffer,
  filterProviderOffers,
} from "../lib/provider-import-actions";
import {
  providerChangedFieldPaths,
  providerSelectedFieldPaths,
  withProviderFieldDraft,
} from "../lib/provider-import-field-selection";
import {
  ProviderOfferAction,
  ProviderOfferFilter,
  type ProviderImportScope,
  type ProviderOfferSelection,
  type ProviderFieldDraft,
  type ProviderOfferConfirmation,
  type ProviderOfferDetail,
  type ProviderOfferList,
  type ProviderOfferProgress,
} from "../components/provider-imports/provider-import-types";

export function useProviderImportReview({
  scope,
  data,
  canImport,
  initialOfferId,
}: {
  scope: ProviderImportScope;
  data: ProviderOfferList | undefined;
  canImport: boolean;
  initialOfferId: string | null;
}) {
  const transport = useProviderImportTransport(scope);
  const { loadOffer } = transport;
  const [filter, setFilter] = useState(ProviderOfferFilter.Available);
  const [activeId, setActiveId] = useState<string | null>(initialOfferId);
  const [activeOffer, setActive] = useState<ProviderOfferDetail | null>(null);
  const [fieldDrafts, setFieldDrafts] = useState<
    Record<string, ProviderFieldDraft>
  >({});
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] =
    useState<ProviderOfferConfirmation | null>(null);
  const [progress, setProgress] = useState<ProviderOfferProgress | null>(null);
  const applying = useRef(false);
  const busy = fetching || progress !== null || transport.isPending;
  const canDismiss = canImport && filter === ProviderOfferFilter.Available;
  const offers = (data?.offers ?? []).map((offer) =>
    withProviderFieldDraft({ offer, draft: fieldDrafts[offer.id] }),
  );
  const active = activeOffer
    ? withProviderFieldDraft({
        offer: activeOffer,
        draft: fieldDrafts[activeOffer.id],
      })
    : null;
  const visible = filterProviderOffers({ offers, filter });

  useEffect(() => {
    setFieldDrafts((previous) => {
      const entries = Object.entries(previous);
      if (entries.length === 0) {
        return previous;
      }
      const current = new Map(
        (data?.offers ?? []).map((offer) => [offer.id, offer]),
      );
      if (activeOffer) {
        current.set(activeOffer.id, activeOffer);
      }
      const retained = entries.filter(
        ([id, draft]) =>
          current.get(id)?.expectedVersion === draft.expectedVersion,
      );
      if (retained.length === entries.length) {
        return previous;
      }
      return Object.fromEntries(retained);
    });
  }, [data?.offers, activeOffer]);

  function setFieldSelected({
    path,
    selected,
  }: {
    path: string;
    selected: boolean;
  }) {
    if (!canImport || busy || !activeOffer?.isActionable) {
      return;
    }
    if (
      !supportsCredentialImportFieldSelection(
        activeOffer.credentialType as CredentialType,
      )
    ) {
      return;
    }
    const changedPaths = providerChangedFieldPaths(activeOffer);
    if (!changedPaths.includes(path)) {
      return;
    }
    setFieldDrafts((previous) => {
      const current = withProviderFieldDraft({
        offer: activeOffer,
        draft: previous[activeOffer.id],
      });
      const selectedPaths = new Set(providerSelectedFieldPaths(current));
      if (selected) {
        selectedPaths.add(path);
      } else {
        selectedPaths.delete(path);
      }
      return {
        ...previous,
        [activeOffer.id]: {
          expectedVersion: activeOffer.expectedVersion,
          selectedFieldPaths: changedPaths.filter((entry) =>
            selectedPaths.has(entry),
          ),
        },
      };
    });
  }

  useEffect(() => {
    if (!initialOfferId) {
      return;
    }
    let current = true;
    setFetching(true);
    void loadOffer(initialOfferId)
      .then((offer) => {
        if (current) {
          setActive(offer);
        }
      })
      .catch((cause: unknown) => {
        if (current) {
          setError(
            cause instanceof Error
              ? cause.message
              : "Could not load this provider version.",
          );
        }
      })
      .finally(() => {
        if (current) {
          setFetching(false);
        }
      });
    return () => {
      current = false;
    };
  }, [initialOfferId, loadOffer]);

  async function reviewOffer(id: string) {
    if (busy) {
      return;
    }
    setActiveId(id);
    setActive(null);
    setError(null);
    setFetching(true);
    try {
      setActive(await loadOffer(id));
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not load this provider version.",
      );
    } finally {
      setFetching(false);
    }
  }

  async function refreshList() {
    if (busy) {
      return;
    }
    setError(null);
    setFetching(true);
    try {
      await transport.refresh();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not refresh provider credentials.",
      );
    } finally {
      setFetching(false);
    }
  }

  async function backToList() {
    if (busy) {
      return;
    }
    // Keep the reviewed snapshot until the list refresh finishes, so an older
    // cached list cannot discard choices made against freshly loaded details.
    await refreshList();
    setActiveId(null);
    setActive(null);
  }

  async function applyOffers(input: ProviderOfferConfirmation) {
    if (input.action === ProviderOfferAction.Dismiss && !canDismiss) {
      return;
    }
    if (!canImport || busy || applying.current || input.offers.length === 0) {
      return;
    }
    if (
      input.offers.some((offer) => !canApplyProviderOffer(offer, input.action))
    ) {
      return;
    }
    applying.current = true;
    setProgress({ ...input, completed: 0 });
    setError(null);
    try {
      const completed = await transport.applyOffers({
        ...input,
        onProgress: (count) => setProgress({ ...input, completed: count }),
      });
      const verb =
        input.action === ProviderOfferAction.Import ? "imported" : "dismissed";
      toast.success(
        `${completed} ${completed === 1 ? "update" : "updates"} ${verb}`,
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The save could not be confirmed. Refresh before trying again.",
      );
    } finally {
      setConfirmation(null);
      setProgress(null);
      applying.current = false;
    }
  }

  function requestBulk(action: ProviderOfferAction) {
    if (action === ProviderOfferAction.Dismiss && !canDismiss) {
      return;
    }
    if (!canImport || busy) {
      return;
    }
    const offers = visible.filter((offer) =>
      canApplyProviderOffer(offer, action),
    );
    if (offers.length === 0) {
      return;
    }
    setError(null);
    // Keep these exact records and version tokens until confirmation. A query
    // refresh must not expand the action or accept newly changed data silently.
    setConfirmation({ action, offers });
  }

  return {
    transport,
    filter,
    setFilter,
    offers,
    setFieldSelected,
    activeId,
    active,
    fetching,
    busy,
    error,
    progress,
    confirmation,
    reviewOffer,
    backToList,
    refreshList,
    requestBulk,
    importOffer: (offer: ProviderOfferSelection) =>
      applyOffers({ action: ProviderOfferAction.Import, offers: [offer] }),
    dismissOffer: (offer: ProviderOfferSelection) =>
      applyOffers({ action: ProviderOfferAction.Dismiss, offers: [offer] }),
    confirmBulk: async () => {
      if (confirmation) {
        await applyOffers(confirmation);
      }
    },
    cancelBulk: () => {
      if (!busy) {
        setConfirmation(null);
      }
    },
  };
}
